import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAppStore } from "@/lib/store";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { checkIntaSendPaymentStatus } from "@/lib/api";

export const Route = createFileRoute("/payment-success")({
  head: () => ({
    meta: [
      { title: "Payment Success — PataTasks" },
      { name: "description", content: "Your payment verification status." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: PaymentSuccessPage,
});

function PaymentSuccessPage() {
  const navigate = useNavigate();
  const search = useSearch({ from: "/payment-success" });
  const [status, setStatus] = useState<"verifying" | "success" | "pending" | "error">("verifying");
  const [message, setMessage] = useState("Verifying your payment...");
  const refreshAll = useAppStore((s) => s.refreshAll);

  useEffect(() => {
    const verify = async () => {
      const reference = search.reference as string | undefined;
      if (!reference) {
        setStatus("error");
        setMessage("Missing payment reference.");
        return;
      }

      try {
        const data = await checkIntaSendPaymentStatus(reference);

        if (data.status === "success") {
          setStatus("success");
          setMessage("Payment verified successfully.");
          toast.success("Payment received");
          await refreshAll();
        } else if (data.status === "pending") {
          setStatus("pending");
          setMessage("Payment is still processing. We will update your balance once confirmed.");
        } else {
          setStatus("error");
          setMessage("Payment verification failed. Please contact support if you were charged.");
        }
      } catch {
        setStatus("error");
        setMessage("Unable to verify payment right now. Please check your wallet later.");
      }
    };

    verify();
  }, [search.reference, refreshAll, navigate]);

  useEffect(() => {
    if (status !== "verifying") {
      const timeout = setTimeout(() => {
        const pendingTestId =
          typeof window !== "undefined" ? localStorage.getItem("pending_test_id") : null;
        if (pendingTestId && status === "success") {
          navigate({ to: "/submit-test" });
        } else {
          navigate({ to: "/" });
        }
      }, 3000);
      return () => clearTimeout(timeout);
    }
  }, [status, navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-linear-to-br from-slate-50 via-blue-50/30 to-background px-4">
      <div className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-sm">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
          {status === "verifying" ? (
            <Loader2 className="h-6 w-6 animate-spin" />
          ) : status === "success" ? (
            <CheckCircle2 className="h-6 w-6" />
          ) : status === "pending" ? (
            <Loader2 className="h-6 w-6 animate-spin text-amber-600" />
          ) : (
            <AlertCircle className="h-6 w-6 text-red-600" />
          )}
        </div>

        <h1 className="text-xl font-bold text-foreground">
          Payment{" "}
          {status === "success" ? "Successful" : status === "pending" ? "Processing" : "Status"}
        </h1>
        <p className="mt-2 text-sm text-slate-600">{message}</p>
        {search.reference && (
          <p className="mt-1 break-all text-xs text-slate-500">
            Reference: {String(search.reference)}
          </p>
        )}

        <div className="mt-6 flex flex-col gap-2">
          <Button className="w-full" onClick={() => navigate({ to: "/" })}>
            Go to Home
          </Button>
          <Button variant="outline" className="w-full" onClick={() => navigate({ to: "/wallet" })}>
            Go to Wallet
          </Button>
        </div>
      </div>
    </div>
  );
}
